// The rule that puts a game's ending in one case, the first that matches:
// Won: the reader won. Lost on time: lost by timeout, whatever the position.
// Slipped: not won, and the reader stood winning before one of their ending
// moves (a draw agreed while winning included). Missed: not won, never
// winning there, but one of the reader's ending moves passed over a winning
// line. Lost: lost, no winning chance in the ending. Drawn: drawn, no
// winning chance. Pure: the caller (api-insights' fact extractor, which
// reads the scan) supplies what each of the reader's ending moves was.
import type { EndingCase, GameFacts } from '@chess/api-model';

/** One of the reader's moves in the ending, as the rule needs it. */
export interface EndingMove {
  /** The reader's score before the move, in pawns (capped); undefined when unknown. */
  before?: number;
  /** The move was a miss whose best line wins (reaches the Winning setting, or mates). */
  missedWin: boolean;
}

/** The case for a game that reached an ending, from its outcome, how it ended and the two facts the rule reads. */
export function endingCaseOf(outcome: GameFacts['outcome'], ending: GameFacts['ending'], stoodWinning: boolean, missedWin: boolean): EndingCase {
  if (outcome === 'win') return 'won';
  if (outcome === 'loss' && ending === 'time') return 'time';
  if (stoodWinning) return 'slipped';
  if (missedWin) return 'missed';
  return outcome === 'loss' ? 'lost' : 'drawn';
}

/** The case from the reader's ending moves: stood winning before any of them (at `winningPawns` or more), or missed a winning line. */
export function classifyEnding(outcome: GameFacts['outcome'], ending: GameFacts['ending'], moves: readonly EndingMove[], winningPawns: number): EndingCase {
  const stood = moves.some((m) => m.before !== undefined && m.before >= winningPawns);
  const missed = moves.some((m) => m.missedWin);
  return endingCaseOf(outcome, ending, stood, missed);
}
